import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  useToast,
  Text,
  FormLabel,
  FormControl,
  Checkbox,
  HStack,
  Icon,
  RadioGroup,
  Stack,
  Radio,
  Textarea,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { BrandButton } from "../BrandButton/BrandButton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import { useParams } from "react-router-dom";
import {
  fetchCustomDataTypesByApplication,
  fetchDataTypes,
} from "../../services/state/custom-data-types/customDataTypes-async-calls";
import { DropDown, Option } from "../Select/DropDown";
import { ExpressionEditorModal } from "../ExpressionEditorModal/ExpressionEditorModal";
import { v4 } from "uuid";
import { setProcessVariables } from "../../services/state/Process-modular/processModular";
import { fetchRecordTypesByApplication } from "../../services/state/record-types/recordTypes-async-calls";
import { BiEdit } from "react-icons/bi";

export const AddProcessVaraibleModal = ({
  isOpen,
  onClose,
  edit = false,
  ProcessVariable,
}: {
  isOpen: boolean;
  onClose: () => void;
  edit?: boolean;
  ProcessVariable?: any;
}) => {
  const initialRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { applicationId } = useParams();

  const [name, setName] = useState("");
  const [type, setType] = useState<
    | {
        id: string;
        name: string;
        component: JSX.Element;
      }
    | undefined
  >(undefined);
  const [value, setValue] = useState<any>("");
  const [isParam, setIsParam] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [isMultiple, setIsMultiple] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [cdt, setcdt] = useState<
    | {
        id: string;
        name: string;
        component: JSX.Element;
      }
    | undefined
  >(undefined);
  const [recordType, setRecordType] = useState<
    | {
        id: string;
        name: string;
        component: JSX.Element;
      }
    | undefined
  >(undefined);
  const [error, setError] = useState<string | null>(null);

  const {
    isOpen: isExpressionEditorModalOpen,
    onOpen: onExpressionEditorModalOpen,
    onClose: onExpressionEditorModalClose,
  } = useDisclosure();

  const selectedProcessModel = useSelector(
    (state: RootState) => state.processes.selectedProcess
  );

  const dataTypes = useSelector(
    (state: RootState) => state.customDataTypes.dataTypes
  );

  const dataTypeOptions = dataTypes.map((type) => ({
    id: type.dataTypCd,
    name: type.dataTypNm,
    component: <Text>{type.dataTypNm}</Text>,
  }));

  const selectedProcess = useSelector(
    (state: RootState) => state.processes.selectedProcess
  );

  const processVariables = useSelector(
    (state: RootState) => state.processModeler.processVariables
  );

  const customDataTypes = useSelector(
    (state: RootState) => state.customDataTypes.customDataTypes
  );

  const customDataTypesOptions = customDataTypes.map((cdt) => ({
    id: cdt.uuid,
    name: cdt.dataTypeName,
    component: <Text>{cdt.dataTypeName}</Text>,
  }));

  const recordTypes = useSelector(
    (state: RootState) => state.recordTypes.recordTypes
  );

  const recordTypeOptions = recordTypes.map((rt) => ({
    id: rt.uuid,
    name: rt.recordName,
    component: <Text>{rt.recordName}</Text>,
  }));

  let selectedProcessModelJson = useSelector(
    (state: RootState) => state.processModeler.selectedProcessModelJson
  );

  const isValidJson = (str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  };

  const handleValue = (value: string) => {
    setValue(value);
  };

  const resetModal = () => {
    setName("");
    setType(undefined);
    setValue("");
    setIsParam(false);
    setIsRequired(false);
    setIsMultiple(false);
    setIsHidden(false);
    setcdt(undefined);
  };

  const validateDefaultValue = (value: any, typeId: string): boolean => {
    const validators: Record<string, (value: any) => boolean> = {
      BOOL: (value) => value === "true" || value === "false",
      INT: (value) => !isNaN(value) && Number.isInteger(Number(value)),
      DOUB: (value) => !isNaN(value),
      STR: (value) => typeof value === "string",
      DATE: () => true,
      DATE_TIME: () => true,
      ANY: (value) => isValidJson(value),
      DOCUMENT: (value) => typeof value === "object" && value instanceof File,
      "BASE 64": (value) => typeof value === "object" && value instanceof File,
    };

    // Return the validator function or default to always true
    return validators[typeId]?.(value) ?? true;
  };

  const onCreate = async () => {
    setError("");

    if (!name) {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (type && !validateDefaultValue(value, type.id)) {
      toast({
        title: "Invalid Default Value",
        description: `The default value does not match the selected type: ${type.name}`,
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Continue with the existing onCreate logic
    const data = {
      uuid: v4(),
      processUuid: selectedProcessModel?.uuid,
      varNm: name,
      description: name,
      dataTypeCd: type?.id,
      varDefVal: value,
      isMultiple,
      isParam,
      isMandatory: isRequired,
      ...(type?.id === "ANY" && cdt?.id && { otherDtUuid: cdt.id }),
      ...(type?.id === "RT" &&
        recordType?.id && { otherDtUuid: recordType.id }),
    };

    try {
      const updatedPvs = [...processVariables, data];
      dispatch(setProcessVariables(updatedPvs));
      resetModal();
      onClose();
    } catch (error) {
      console.error("Error creating process:", error);
      toast({
        title: "Error",
        description:
          "An error occurred while creating a new process variable. Please try again.",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onUpdate = async () => {
    if (type && !validateDefaultValue(value, type.id)) {
      toast({
        title: "Invalid Default Value",
        description: `The default value does not match the selected type: ${type.name}`,
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Continue with the existing onUpdate logic
    const data = {
      uuid: ProcessVariable.uuid,
      processUuid: ProcessVariable.processUuid,
      varNm: name,
      description: name,
      dataTypeCd: type?.id,
      varDefVal: value,
      isMultiple,
      isParam,
      isMandatory: isRequired,
      ...(type?.id === "ANY" && cdt?.id && { otherDtUuid: cdt.id }),
      ...(type?.id === "RT" &&
        recordType?.id && { otherDtUuid: recordType.id }),
    };

    const updatedProcessVariables = processVariables.filter(
      (pv) => pv.uuid !== ProcessVariable.uuid
    );

    const updatedPvs = [...updatedProcessVariables, data];
    dispatch(setProcessVariables(updatedPvs));
    resetModal();
    onClose();
  };

  const resetForm = () => {
    setName("");
    setType(undefined);
    setValue("");
    setIsParam(false);
    setIsRequired(false);
    setIsMultiple(false);
    setIsHidden(false);
  };

  const renderInputField = () => {
    switch (type?.id) {
      case "BOOL":
        return (
          <RadioGroup onChange={setValue} value={value} width={"100%"}>
            <Stack direction="column" textAlign={"left"}>
              <Radio value="true" colorScheme="brand">
                True
              </Radio>
              <Radio value="false" colorScheme="brand">
                False
              </Radio>
            </Stack>
          </RadioGroup>
        );
      case "INT":
      case "DOUB":
      case "STR":
        return (
          <HStack>
            <Input
              placeholder={`Enter a ${type?.id === "INT" ? "number" : "text"}`}
              focusBorderColor="brand.600"
              fontSize="18px"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              width="70%"
            />
            <Icon
              as={BiEdit as unknown as React.ElementType}
              boxSize={"24px"}
              color={"brand.500"}
              onClick={onExpressionEditorModalOpen}
            />
          </HStack>
        );

      case "DATE":
        return (
          <Input
            placeholder="Select Date and Time"
            size="md"
            type="date"
            onChange={(e) => setValue(e.target.value)}
          />
        );
      case "DATE_TIME":
        return (
          <Input
            placeholder="Select Date and Time"
            size="md"
            type="datetime-local"
            onChange={(e) => setValue(e.target.value)}
            bg="rgba(255, 255, 255, 0.1)"
            border="1px solid rgba(255, 255, 255, 0.3)"
            color="white"
            _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
            _focus={{
              borderColor: "brand.500",
              boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.6)",
            }}
          />
        );
      case "ANY":
        return (
          <Textarea
            placeholder="Enter JSON"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            bg="rgba(255, 255, 255, 0.1)"
            border="1px solid rgba(255, 255, 255, 0.3)"
            color="white"
            _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
            _focus={{
              borderColor: "brand.500",
              boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.6)",
            }}
          />
        );
      case "DOCUMENT":
      case "BASE 64":
        return (
          <Input
            type="file"
            onChange={(e) =>
              setValue(e.target.files ? e.target.files[0] : null)
            }
            bg="rgba(255, 255, 255, 0.1)"
            border="1px solid rgba(255, 255, 255, 0.3)"
            color="white"
            _focus={{
              borderColor: "brand.500",
              boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.6)",
            }}
          />
        );
      default:
        return (
          <HStack>
            <Input
              fontSize="18px"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              width="70%"
              bg="rgba(255, 255, 255, 0.1)"
              border="1px solid rgba(255, 255, 255, 0.3)"
              color="white"
              _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
              _focus={{
                borderColor: "brand.500",
                boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.6)",
              }}
            />
            <Icon
              as={BiEdit as unknown as React.ElementType}
              boxSize={"24px"}
              color={"brand.500"}
              onClick={onExpressionEditorModalOpen}
            />
          </HStack>
        );
    }
  };

  useEffect(() => {
    dispatch(fetchDataTypes());
    dispatch(
      fetchCustomDataTypesByApplication(selectedProcess?.app.uuid as string)
    );
    dispatch(
      fetchRecordTypesByApplication(selectedProcess?.app.uuid as string)
    );
  }, [isOpen]);

  useEffect(() => {
    if (edit && ProcessVariable) {
      setName(ProcessVariable.varNm);
      setType(
        dataTypeOptions.find((type) => type.id === ProcessVariable.dataTypeCd)
      );
      setcdt(
        customDataTypesOptions.find(
          (cdt) => cdt.id === ProcessVariable.otherDtUuid
        )
      );
      setRecordType(
        recordTypeOptions.find((rt) => rt.id === ProcessVariable.otherDtUuid)
      );
      setValue(ProcessVariable.varDefVal);
      setIsParam(ProcessVariable.isParam);
      setIsRequired(ProcessVariable.isMandatory);
      setIsMultiple(ProcessVariable.isMultiple);
      setIsHidden(ProcessVariable.isParam);
    }
  }, [edit, ProcessVariable]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={initialRef}
      size="3xl"
      isCentered
    >
      <ModalOverlay
        zIndex={1}
        backdropFilter="blur(10px)" // ← This will blur the page behind the modal
        bg="rgba(0, 0, 0, 0.4)"
      />
      <ModalContent
        bg="rgba(0, 0, 0, 0.9)"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255, 255, 255, 0.4)"
        borderRadius="12px"
        color="white"
      >
        <ModalCloseButton color="white" />
        <ModalBody>
          {edit ? (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Edit Process Variable
            </Text>
          ) : (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Create New Process Variable
            </Text>
          )}
          <FormControl isRequired>
            <HStack justifyContent="space-between">
              <FormLabel minWidth="30%" textAlign="right" color="white">
                Name
              </FormLabel>

              <Input
                placeholder="Enter name of the process"
                ref={initialRef}
                fontSize="18px"
                value={name}
                onChange={(e) => setName(e.target.value)}
                isRequired
                width="70%"
                bg="rgba(255, 255, 255, 0.1)"
                border="1px solid rgba(255, 255, 255, 0.3)"
                color="white"
                _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.6)",
                }}
              />
            </HStack>
          </FormControl>

          <FormControl mt={3}>
            <HStack justifyContent="space-between">
              <FormLabel minWidth="30%" textAlign="right" color="white">
                Type
              </FormLabel>
              <DropDown
                selected={
                  type
                    ? {
                        id: type.id,
                        name: type.name,
                        component: type.component,
                      }
                    : undefined
                }
                title={<HStack justifyContent="space-between" p={2}></HStack>}
                options={dataTypeOptions.map((dataType) => {
                  return {
                    id: dataType.id,
                    name: dataType.name,
                    component: dataType.component,
                  };
                })}
                onOptionChange={function (
                  selectedOptions: Option | Option[]
                ): void {
                  if (!Array.isArray(selectedOptions)) {
                    setType(selectedOptions);
                  }
                }}
                fontSize="18px"
              />
            </HStack>
          </FormControl>
          {type?.id === "RT" && (
            <FormControl mt={3}>
              <HStack justifyContent="space-between">
                <FormLabel minWidth="30%" textAlign="right" color="white">
                  Record Type
                </FormLabel>
                <DropDown
                  selected={
                    recordType
                      ? {
                          id: recordType.id,
                          name: recordType.name,
                          component: recordType.component,
                        }
                      : undefined
                  }
                  title={<HStack justifyContent="space-between" p={2}></HStack>}
                  options={recordTypeOptions.map((rt) => {
                    return {
                      id: rt.id,
                      name: rt.name,
                      component: rt.component,
                    };
                  })}
                  onOptionChange={function (
                    selectedOptions: Option | Option[]
                  ): void {
                    if (!Array.isArray(selectedOptions)) {
                      setRecordType(selectedOptions);
                    }
                  }}
                  fontSize="18px"
                />
              </HStack>
            </FormControl>
          )}
          {type?.id === "ANY" && (
            <FormControl mt={3}>
              <HStack justifyContent="space-between">
                <FormLabel minWidth="30%" textAlign="right" color="white">
                  Custom dataType
                </FormLabel>
                <DropDown
                  selected={
                    cdt
                      ? {
                          id: cdt.id,
                          name: cdt.name,
                          component: cdt.component,
                        }
                      : undefined
                  }
                  title={<HStack justifyContent="space-between" p={2}></HStack>}
                  options={customDataTypesOptions.map((cdt) => {
                    return {
                      id: cdt.id,
                      name: cdt.name,
                      component: cdt.component,
                    };
                  })}
                  onOptionChange={function (
                    selectedOptions: Option | Option[]
                  ): void {
                    if (!Array.isArray(selectedOptions)) {
                      setcdt(selectedOptions);
                    }
                  }}
                  fontSize="18px"
                />
              </HStack>
            </FormControl>
          )}

          <FormControl mt={3}>
            <HStack>
              <FormLabel minWidth="30%" textAlign="right" color="white">
                Value
              </FormLabel>
              <VStack>{renderInputField()}</VStack>
            </HStack>
          </FormControl>

          <FormControl mt={3}>
            <HStack justifyContent="space-between">
              <FormLabel minWidth="30%" textAlign="right" color="white">
                Multiple
              </FormLabel>
              <Checkbox
                isChecked={isMultiple}
                onChange={(e) => setIsMultiple(e.target.checked)}
                width="70%"
                colorScheme="brand"
              >
                <Text color="white">
                  Value for the Variable is array for selected data type
                </Text>
              </Checkbox>
            </HStack>
          </FormControl>

          <FormControl mt={3}>
            <HStack justifyContent="space-between">
              <FormLabel minWidth="30%" textAlign="right" color="white">
                Parameter
              </FormLabel>
              <Checkbox
                isChecked={isParam}
                onChange={(e) => setIsParam(e.target.checked)}
                width="70%"
                colorScheme="brand"
              >
                <Text color="white">
                  Allow the value for the Variable to be provided when starting
                  a process
                </Text>
              </Checkbox>
            </HStack>
          </FormControl>

          <FormControl mt={3}>
            <HStack justifyContent="space-between">
              <FormLabel minWidth="30%" textAlign="right" color="white">
                Required
              </FormLabel>
              <Checkbox
                isChecked={isRequired}
                onChange={(e) => setIsRequired(e.target.checked)}
                width="70%"
                colorScheme="brand"
              >
                <Text color="white">Make the Variable required</Text>
              </Checkbox>
            </HStack>
          </FormControl>

          <FormControl mt={3}>
            <HStack justifyContent="space-between">
              <FormLabel minWidth="30%" textAlign="right" color="white">
                Hidden
              </FormLabel>
              <Checkbox
                isChecked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
                width="70%"
                colorScheme="brand"
              >
                <Text color="white">Hide the Variable from users</Text>
              </Checkbox>
            </HStack>
          </FormControl>
        </ModalBody>
        <ExpressionEditorModal
          isOpen={isExpressionEditorModalOpen}
          onClose={onExpressionEditorModalClose}
          value={value}
          handleValue={handleValue}
        />
        <ModalFooter>
          {edit ? (
            <BrandButton onClick={onUpdate}>Save Changes</BrandButton>
          ) : (
            <BrandButton onClick={onCreate}>Create</BrandButton>
          )}{" "}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
